import { ILanguageExtended } from "@multiweb/types/contracts";
import { TypedObject } from "@multiweb/types/react";
import { CLEAR_LOCALIZATION_ITEMS, CONCAT_LOCALIZATION_ITEMS, LOAD_LOCALIZATION_KEYS, LOCALIZATION_KEYS_LOADED, SET_LOCALE_CODE, SET_SUPPORTED_LANGUAGES } from "./constants";
import { IClearLocalizationItemsAction, IConcatLocalizationItemsAction, ILoadLocalizationKeysAction, ILocalizationKeysLoadedAction, ISetLocaleCodeAction, ISetSupportedLanguagesAction, LocalizationItem } from "./types";

/**
 * Akce pro načtení hodnot pro lokalizační klíče. I18n modul obsluhuje nastavení načítacího indikátoru pro zadaný klíč.
 *
 * @param localizationKeys Klíče, pro které se načtou lokalizační data.
 * @param fetchingKey Klíč pro fetching modul, pod kterým bude indikováno načítání.
 * @param enableBatching Příznak, zda se má využít batchování akcí. Výchozí hodnota je false. Batchování umožňuje zpracovat akce následující krátce za sebou najednou,
 * čímž šetří výkon. Vhodné pro použití v kombinaci s {@link LocalizationLoader}. Nehodí se pro osamocené akce, kde nepřinese benefit výkonu a pouze prodlouží načtení
 * o dobu čekání na následné akce.
 */
export const createLoadLocalizationKeysAction = (localizationKeys: readonly string[], fetchingKey: string, enableBatching = false): ILoadLocalizationKeysAction => ({
    type: LOAD_LOCALIZATION_KEYS,
    payload: {
        fetchingKey,
        localizationKeys,
        enableBatching,
    },
});

/**
 * Akce indikující, že načtení hodnot pro lokalizační klíče proběhlo v pořádku.
 * Tuto akci je možné spárovat s původním požadavkem pomocí fetchingKey.
 * @param fetchingKey Klíč pro fetching modul, který byl použit v akci pro načtení lokalizace.
 */
export const createLocalizationKeysLoadedAction = (fetchingKey: string): ILocalizationKeysLoadedAction => ({
    type: LOCALIZATION_KEYS_LOADED,
    payload: {
        fetchingKey,
    },
});

/**
 * Akce pro nastavení jazyka aplikace.
 * @param localeCode Kód jazyka aplikace
 */
export const createSetLocaleCodeAction = (localeCode: string): ISetLocaleCodeAction => ({
    type: SET_LOCALE_CODE,
    payload: {
        localeCode,
    },
});

/**
 * Akce pro nastavení podporovaných jazyků aplikace.
 * @param supportedLanguages Podporované jazyky.
 */
export const createSetSupportedLanguagesAction = (supportedLanguages: readonly ILanguageExtended[]): ISetSupportedLanguagesAction => ({
    type: SET_SUPPORTED_LANGUAGES,
    payload: {
        supportedLanguages,
    },
});

/**
 * Akce pro doplnění lokalizačních položek.
 * @param localizationItems Objekt s položkami lokalizace.
 */
export const createConcatLocalizationItemsAction = (localizationItems: TypedObject<LocalizationItem>): IConcatLocalizationItemsAction => ({
    type: CONCAT_LOCALIZATION_ITEMS,
    payload: {
        localizationItems,
    },
});

/**
 * Akce pro vyprázdnění lokalizačních položek.
 */
export const createClearLocalizationItemsAction = (): IClearLocalizationItemsAction => ({
    type: CLEAR_LOCALIZATION_ITEMS,
});
